import ApiClient from '@afosto/api/ApiClient';

class Logistics {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Create a new transfer
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a new transfer
   * @returns {Promise}
   */
  createTransfer(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Create a new transfer projection
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  createTransferProjection(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers/projection`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Get a sub transfer
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a sub transfer
   * @returns {Promise}
   */
  getSubTransfer(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/subtransfers/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a subtransfer items
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a sub transfer items
   * @returns {Promise}
   */
  getSubTransferItems(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/subtransfers/${id}/items`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of sub transfers
   *
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  getSubTransfers(options = {}) {
    return this.apiClient.request({
      url: `/wms/subtransfers`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a transfer
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  getTransfer(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of transfers
   *
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  getTransfers(options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of transports belonging to the transfer
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description a list of transports for the given transfer
   * @returns {Promise}
   */
  getTransferTransports(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers/${id}/transports`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of transported items
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description returns a describing lsit of transfer items with their route
   * @returns {Promise}
   */
  getTransferTransportsItems(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers/${id}/items`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of transported items summary
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.claimId] - filter for a specific claim_id
   * @param {Object} options - The additional request options
   * @description returns a describing lsit of transfer items with their route
   * @returns {Promise}
   */
  getTransferTransportsItemsSummary(id, query = {}, options = {}) {
    const params = {
      claim_id: query.claimId,
    };

    return this.apiClient.request({
      url: `/wms/transfers/${id}/summary`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Report a set of items at a location
   *
   * @param {Object} body - Transfer request object
   * @param {Object} options - The additional request options
   * @description reports claim items at a certain location
   * @returns {Promise}
   */
  reportClaimItems(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/report`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Attach a shipment to the transport
   *
   * @param {string} id
   * @param {string} transferId
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  updateTransferTransportShipment(transferId, id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers/${transferId}/transports/${id}/shipment`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update transport state
   *
   * @param {string} id
   * @param {string} transferId
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  updateTransferTransportState(transferId, id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/transfers/${transferId}/transports/${id}/state`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const logistics = new Logistics();

export default Logistics;
